import { Injectable } from '@angular/core';
import { SimpleUser } from 'app/models';
import { Observable, of } from 'rxjs';
import { LikeApi } from '../api/like.api';
import { UsersLikeHttpResult } from '../models/like.model';
import { ActivityMode, ActivitySessionCompleted, TimelineElement, TimelineElementType } from '../models/timeline.model';

export enum LikeApiType {
  ACTIVITY,
  PLAYLIST,
  VIDEO,
}

@Injectable({
  providedIn: 'root'
})
export class LikeService {
  constructor(private readonly likeApi: LikeApi) {}

  getUserLikes(timelineElement: TimelineElement, page: number): Observable<UsersLikeHttpResult> {
    const apiType = this.determineLikeApiFromElement(timelineElement);

    if (apiType === LikeApiType.ACTIVITY) {
      const { activity_id, id } = timelineElement.subject as ActivitySessionCompleted;
      return this.likeApi.getUserLikedActivity(activity_id, id, page);
    }
    if (apiType === LikeApiType.PLAYLIST) {
      const { id } = timelineElement.subject;
      return this.likeApi.getUserLikedPlaylist(id, page);
    }
    if (apiType === LikeApiType.VIDEO) {
      const { id } = timelineElement.subject;
      return this.likeApi.getUserLikedVideo(id, page);
    }

    console.warn(`No api method found to get likes from element type '${apiType}'`);
    return of(null);
  }

  likeElement(timelineElement: TimelineElement): Observable<any> {
    const apiType = this.determineLikeApiFromElement(timelineElement);
    if (apiType === LikeApiType.ACTIVITY) {
      const { activity_id, id } = timelineElement.subject as ActivitySessionCompleted;
      return this.likeApi.postLikeActivity(activity_id, id);
    }
    if (apiType === LikeApiType.PLAYLIST) {
      const { id } = timelineElement.subject;
      return this.likeApi.postLikePlaylist(id);
    }
    if (apiType === LikeApiType.VIDEO) {
      const { id } = timelineElement.subject;
      return this.likeApi.postLikeVideo(id);
    }

    console.warn(`No api method found to add likes from element type '${apiType}'`);
    return of(null);
  }

  unLikeElement(timelineElement: TimelineElement): Observable<any> {
    const apiType = this.determineLikeApiFromElement(timelineElement);
    if (apiType === LikeApiType.ACTIVITY) {
      const { activity_id, id } = timelineElement.subject as ActivitySessionCompleted;
      return this.likeApi.deleteLikeActivity(activity_id, id);
    }
    if (apiType === LikeApiType.PLAYLIST) {
      const { id } = timelineElement.subject;
      return this.likeApi.deleteLikePlaylist(id);
    }
    if (apiType === LikeApiType.VIDEO) {
      const { id } = timelineElement.subject;
      return this.likeApi.deleteLikeVideo(id);
    }

    console.warn(`No api method found to unlike from element type '${apiType}'`);
    return of(null);
  }

  private determineLikeApiFromElement(element: TimelineElement): LikeApiType {
    if (element.type === TimelineElementType.ACTIVITY_SESSION_COMPLETED) {
      return LikeApiType.ACTIVITY;
    }
    if ([
      TimelineElementType.VIDEO_ADDED,
      TimelineElementType.VIDEO_COACHING_ADDED
    ].includes(element.type)) {
      return LikeApiType.VIDEO;
    }
    if (element.type === TimelineElementType.PLAYLIST_UNLOCKED) {
      return LikeApiType.PLAYLIST;
    }
  }
}
